import React, { useEffect, useRef, useState } from "react";
import {
  FaArrowLeftLong,
  FaArrowRightLong,
  FaCircleArrowRight,
  FaCirclePlay,
} from "react-icons/fa6";
import { Pagination, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";
import { Helmet } from "react-helmet";

const hasText = (value) => Boolean(value && String(value).trim());

const Hero = () => {
  const [sliders, setSliders] = useState([]);
  const swiperRef = useRef(null);

  useEffect(() => {
    const callSliders = async () => {
      try {
        const res = await fetch(
          process.env.REACT_APP_BASE_API_URL + "/sliders",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        const data = await res.json();
        setSliders(Array.isArray(data) ? data : []);
        if (!res.status === 200) {
          const error = new Error(res.error);
          throw error;
        }
      } catch (err) {
        console.log(err);
      }
    };
    callSliders();
  }, []);

  return (
    <div className="z-0 w-full overflow-x-hidden">
      <Helmet>
        <title>
          Mission Organ Protection | An Initiative by Micro Labs Limited
        </title>
        <meta
          name="description"
          content="Mission Organ Protection is an initiative by Micro Labs Limited focusing on diabetes awareness and related healthcare solutions."
        />
      </Helmet>

      {/* Navbar-colored top pad — slightly more on mobile so banner clears the logo */}
      <div className="w-full bg-[#258695] pt-[96px] sm:pt-[92px] md:pt-[88px] lg:pt-[92px]">
        <div className="relative w-full overflow-hidden bg-secondary">
          {sliders.length > 0 && (
            <Swiper
              key={sliders.map((s) => s.id).join("-")}
              modules={[Pagination, Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              autoHeight
              rewind={sliders.length > 1}
              loop={false}
              resistanceRatio={0}
              pagination={{ clickable: true }}
              autoplay={
                sliders.length > 1
                  ? {
                      delay: 3000,
                      disableOnInteraction: false,
                    }
                  : false
              }
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              className="hero-swiper w-full"
            >
              {sliders.map((slider) => {
                const title = hasText(slider.title) ? slider.title.trim() : "";
                const caption = hasText(slider.caption)
                  ? slider.caption.trim()
                  : "";
                const description = hasText(slider.description)
                  ? slider.description.trim()
                  : "";
                const showButtonOne =
                  hasText(slider.button_one_text) &&
                  hasText(slider.button_one_link) &&
                  slider.button_one_link.trim() !== "#";
                const showButtonTwo =
                  hasText(slider.button_two_text) &&
                  hasText(slider.button_two_link) &&
                  slider.button_two_link.trim() !== "#";
                const showOverlay =
                  title ||
                  caption ||
                  description ||
                  showButtonOne ||
                  showButtonTwo;

                return (
                  <SwiperSlide key={slider.id}>
                    <div className="relative w-full bg-secondary">
                      <img
                        src={slider.image}
                        alt={caption || title || "Hero banner"}
                        className="block w-full h-auto align-middle"
                        draggable={false}
                      />

                      {showOverlay && (
                        <div className="absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] sm:w-[70%] md:w-[55%] lg:w-[48%] xl:w-[40%] py-2 sm:py-3 rounded-lg sm:rounded-xl flex flex-col justify-center items-center text-center px-3 sm:px-4 md:px-10 lg:px-16 bg-gradient-to-r from-teal-800/80 to-teal-600/80">
                          {title && (
                            <span className="xl:text-lg md:text-xl text-[10px] sm:text-sm text-white font-inter font-light tracking-wider line-clamp-1">
                              {title}
                            </span>
                          )}
                          {caption && (
                            <h1 className="text-white font-inter text-sm sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-semibold mt-1 md:mt-3 line-clamp-2">
                              {caption}
                            </h1>
                          )}
                          {description && (
                            <p className="text-white w-[95%] xl:text-base md:text-sm hidden md:block font-inter font-normal lg:mt-5 xl:mt-8 line-clamp-3">
                              {description}
                            </p>
                          )}
                          {(showButtonOne || showButtonTwo) && (
                            <div className="flex flex-col sm:flex-row items-center justify-center mt-2 md:mt-0 gap-2 sm:gap-4">
                              {showButtonOne && (
                                <button
                                  onClick={() => {
                                    window.open(
                                      slider.button_one_link,
                                      "_self"
                                    );
                                  }}
                                  className="bg-white flex items-center gap-2 text-secondary font-inter font-semibold text-[10px] sm:text-xs lg:text-base lg:mt-8 md:mt-5 p-1.5 sm:p-2 px-3 sm:px-5 rounded-full hover:bg-primary hover:text-white whitespace-nowrap"
                                >
                                  {slider.button_one_text.trim()}
                                  <FaCircleArrowRight />
                                </button>
                              )}
                              {showButtonTwo && (
                                <button
                                  onClick={() => {
                                    window.open(
                                      slider.button_two_link,
                                      "_self"
                                    );
                                  }}
                                  className="bg-secondary flex items-center gap-2 text-white hover:bg-white hover:text-secondary font-inter font-semibold text-[10px] sm:text-xs lg:text-base lg:mt-8 md:mt-5 p-1.5 sm:p-3 px-3 sm:px-5 rounded-full whitespace-nowrap"
                                >
                                  {slider.button_two_text.trim()}
                                  <FaCirclePlay />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}

          {sliders.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous slide"
                onClick={() => swiperRef.current?.slidePrev()}
                className="hidden sm:flex absolute z-30 top-1/2 -translate-y-1/2 left-3 lg:left-6 items-center justify-center rounded-full cursor-pointer border-2 border-white/40 p-2 sm:p-2.5 bg-navbar/95 hover:bg-navbar text-white shadow-md"
              >
                <FaArrowLeftLong className="text-sm sm:text-xl" />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                onClick={() => swiperRef.current?.slideNext()}
                className="hidden sm:flex absolute z-30 top-1/2 -translate-y-1/2 right-3 lg:right-6 items-center justify-center rounded-full cursor-pointer border-2 border-white/40 p-2 sm:p-2.5 bg-navbar/95 hover:bg-navbar text-white shadow-md"
              >
                <FaArrowRightLong className="text-sm sm:text-xl" />
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        .hero-swiper {
          width: 100%;
          overflow: hidden;
        }
        .hero-swiper .swiper-slide {
          width: 100%;
        }
        .hero-swiper .swiper-pagination {
          bottom: 8px !important;
        }
        .hero-swiper .swiper-pagination-bullet {
          background: #fff;
          opacity: 0.65;
        }
        .hero-swiper .swiper-pagination-bullet-active {
          background: #258695;
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default Hero;
